'use client';

import { Component as ReactComponent, Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { FoldHorizontal, UnfoldHorizontal } from 'lucide-react';
import { WIDGET_REGISTRY } from '../widgets/registry';
import { WidgetCard } from './widget-card';
import { WidgetErrorState } from '../widgets/empty-states';
import type { DashboardLayout } from '../widgets/types';

/** Un widget care aruncă la randare nu mai dărâmă TOT dashboard-ul (audit
 *  2026-07-13) — cade doar cardul lui, cu mesaj + retry (remount prin key). */
class WidgetBoundary extends ReactComponent<{ children: ReactNode }, { failed: boolean; attempt: number }> {
  state = { failed: false, attempt: 0 };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error: unknown) {
    console.error('[dashboard] widget crash:', error);
  }
  render() {
    if (this.state.failed) {
      return (
        <WidgetErrorState
          onRetry={() => this.setState((s) => ({ failed: false, attempt: s.attempt + 1 }))}
        />
      );
    }
    // Fragment cu key: remount curat al widgetului la „Reîncearcă", fără DOM
    // suplimentar care ar strica layout-ul flex din card.
    return <Fragment key={this.state.attempt}>{this.props.children}</Fragment>;
  }
}

declare global {
  interface Window {
    Sortable?: any;
  }
}

const SORTABLE_CDN = 'https://cdn.jsdelivr.net/npm/sortablejs@1.15.0/Sortable.min.js';

// v2 (2026-07-12, decizie owner): implicit doar setul „esențial" — banii
// (facturat/încasat/neîncasat/restant), obligațiile fiscale (alerte, ANAF,
// declarații) și acțiunile zilnice. Restul widget-urilor (DSO, YoY, prognoze,
// what-if, recurențe, activitate etc.) rămân disponibile din „Widget-uri".
// EXPORTAT: page.tsx e SINGURUL loc care instanțiază useDashboardLayout cu
// default-ul ăsta — istoric, pagina avea o A DOUA instanță de hook cu default
// WIDGET_ORDER (toate widget-urile) pe aceeași cheie localStorage, iar efectul
// ei de persist rula ULTIMUL → pe cont proaspăt se salva lista completă și
// migrarea v1→v2 n-o mai recunoștea (default-ul lean era anulat la reload).
export const DEFAULT_LAYOUT: DashboardLayout = {
  visible: [
    'revenue-month',
    'collected-month',
    'outstanding',
    'overdue-kpi',
    'alerts',
    'anaf-status',
    'fiscal-calendar',
    'revenue-chart',
    'recent-invoices',
    'top-debitors',
    'quick-actions',
  ],
  hidden: [],
  v: 2,
};

function loadSortable(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return reject(new Error('no window'));
    if (window.Sortable) return resolve();
    const existing = document.querySelector<HTMLScriptElement>('script[data-sortable]');
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      return;
    }
    const s = document.createElement('script');
    s.src = SORTABLE_CDN;
    s.async = true;
    s.setAttribute('data-sortable', '1');
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('failed to load sortable'));
    document.head.appendChild(s);
  });
}

interface Props {
  /** Starea layout-ului vine de la PĂRINTE (page.tsx) — o singură instanță de
   *  useDashboardLayout pentru grid + picker, altfel cele două instanțe se
   *  desincronizează și se suprascriu reciproc în localStorage. */
  layout: DashboardLayout;
  hydrated: boolean;
  setOrder: (order: string[]) => void;
  hideWidget: (id: string) => void;
  /** Modul „Aranjează": grip-ul de drag + X-ul de ascundere apar doar aici;
   *  în afara lui dashboard-ul e curat, doar de citit (drag dezactivat). */
  editMode?: boolean;
  /** Redimensionare per widget (−/+ în modul „Aranjează"): span nou pe treptele
   *  3/4/6/8/12, sau null = înapoi la lățimea implicită a widgetului. */
  onResize?: (id: string, span: number | null) => void;
}

export function WidgetGrid({ layout, hydrated, setOrder, hideWidget, editMode = false, onResize }: Props) {
  const gridRef = useRef<HTMLDivElement>(null);
  const sortableRef = useRef<any>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    loadSortable()
      .then(() => setReady(true))
      .catch(() => setReady(true)); // render without drag if CDN fails
  }, []);

  useEffect(() => {
    if (!ready || !hydrated || !gridRef.current || !window.Sortable) return;
    if (sortableRef.current) return;
    sortableRef.current = window.Sortable.create(gridRef.current, {
      animation: 200,
      easing: 'cubic-bezier(0.2, 0.9, 0.3, 1.2)',
      ghostClass: 'dash-sortable-ghost',
      chosenClass: 'dash-sortable-chosen',
      dragClass: 'dash-sortable-drag',
      handle: '[data-drag-grip]', // drag DOAR de pe grip-ul din antet → restul cardului scrollabil pe touch (fix mobil)
      disabled: !editMode, // drag activ doar în modul „Aranjează"
      onEnd: () => {
        if (!gridRef.current) return;
        const children = Array.from(gridRef.current.children) as HTMLElement[];
        const newOrder = children
          .map((el) => el.getAttribute('data-widget-id'))
          .filter((v): v is string => Boolean(v));
        setOrder(newOrder);
      },
    });
    return () => {
      sortableRef.current?.destroy();
      sortableRef.current = null;
    };
    // editMode NU e în deps: instanța se creează o singură dată, iar toggle-ul
    // se face prin option('disabled') în efectul de mai jos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, hydrated, setOrder]);

  // Toggle drag on/off la schimbarea modului, fără să recreem instanța.
  useEffect(() => {
    sortableRef.current?.option('disabled', !editMode);
  }, [editMode]);

  if (!hydrated) {
    return (
      <div className="grid grid-cols-12 gap-4 min-h-[400px]">
        <div className="col-span-12 h-40 bg-neutral-100 dark:bg-neutral-800 animate-pulse rounded-2xl" />
      </div>
    );
  }

  return (
    <>
      {/* grid-flow-dense = PĂSTRAT deliberat (audit P3): pe default-ul lean
          umple golul de lângă graficul XL cu cardurile mici următoare; fără el
          rămân găuri. Trade-off asumat: ordinea vizuală poate devia ușor de la
          ordinea DOM la lățimi mixte. */}
      <div ref={gridRef} className="grid grid-cols-12 gap-4 grid-flow-dense dash-uniform-rows">
        {layout.visible.map((id) => {
          const def = WIDGET_REGISTRY[id];
          if (!def) {
            // ID vechi rămas în localStorage sau typo în DEFAULT_LAYOUT — fără
            // warn, golul din grid e invizibil și greu de depanat.
            console.warn(`[dashboard] Widget '${id}' lipsește din registry — sărit.`);
            return null;
          }
          const SIZE_TO_SPAN: Record<string, number> = { S: 3, M: 4, L: 6, XL: 8, Full: 12 };
          const SIZE_TO_ROW: Record<string, number> = { S: 1, M: 1, L: 2, XL: 2, Full: 2 };
          const defaultSpan = def.defaultSpan ?? SIZE_TO_SPAN[(def as any).size] ?? 4;
          // Lățimea per widget e personalizabilă din modul „Aranjează"
          // (layout.overrides[id].span) — câmpul exista în tip dar era nefolosit.
          const span = layout.overrides?.[id]?.span ?? defaultSpan;
          const rowSpan = layout.overrides?.[id]?.row ?? def.defaultRow ?? SIZE_TO_ROW[(def as any).size] ?? 1;
          const Component = def.Component;
          // md: (tabletă 768–1024) — înainte lipsea complet: L/XL cădeau pe
          // toată lățimea și iPad-ul arăta o coloană infinită. Acum: S = 3/rând,
          // M/L = 2/rând; graficele late (7+) rămân pe tot rândul până la lg.
          const colClass =
            span === 2 ? 'col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-2'
              : span === 3 ? 'col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3'
              : span === 4 ? 'col-span-12 sm:col-span-6 md:col-span-6 lg:col-span-4'
              : span === 5 ? 'col-span-12 md:col-span-6 lg:col-span-5'
              : span === 6 ? 'col-span-12 md:col-span-6 lg:col-span-6'
              : span === 7 ? 'col-span-12 lg:col-span-7'
              : span === 8 ? 'col-span-12 lg:col-span-8'
              : span === 9 ? 'col-span-12 lg:col-span-9'
              : 'col-span-12';
          const rowClass = rowSpan === 2 ? 'row-span-2' : '';
          // Trepte de lățime pentru butoanele −/+ din modul „Aranjează".
          const LADDER = [3, 4, 6, 8, 12];
          const step = (dir: -1 | 1) => {
            const idx = LADDER.findIndex((s) => s >= span);
            const cur = idx === -1 ? LADDER.length - 1 : idx;
            const next = Math.min(Math.max(cur + dir, 0), LADDER.length - 1);
            const target = LADDER[next]!;
            // Înapoi la implicit → curățăm override-ul (nu stocăm redundant).
            onResize?.(id, target === defaultSpan ? null : target);
          };
          return (
            <div key={id} data-widget-id={id} className={colClass + ' ' + rowClass}>
              <WidgetCard
                id={id}
                title={def.title}
                icon={def.icon}
                isNew={def.isNew}
                onHide={hideWidget}
                showControls={editMode}
                actions={editMode && onResize ? (
                  <span className="flex items-center gap-0.5 mr-0.5">
                    <button
                      type="button"
                      title="Îngustează widgetul"
                      disabled={span <= LADDER[0]!}
                      onClick={() => step(-1)}
                      className="p-1 rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 dark:hover:text-neutral-200 disabled:opacity-30 transition-colors"
                    >
                      <FoldHorizontal className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      title="Lățește widgetul"
                      disabled={span >= 12}
                      onClick={() => step(1)}
                      className="p-1 rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 dark:hover:text-neutral-200 disabled:opacity-30 transition-colors"
                    >
                      <UnfoldHorizontal className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ) : undefined}
              >
                <WidgetBoundary>
                  <Component />
                </WidgetBoundary>
              </WidgetCard>
            </div>
          );
        })}
      </div>

      <style jsx global>{`
        /* Flash scurt pe widgetul proaspăt adăugat din picker (scroll-into-view
           + inel primary care se stinge) — altfel cardul nou ateriza la finalul
           paginii fără niciun semnal vizual. */
        @keyframes dashWidgetFlash {
          0% { box-shadow: 0 0 0 3px rgba(241, 90, 36, 0.55); }
          100% { box-shadow: 0 0 0 3px rgba(241, 90, 36, 0); }
        }
        .dash-widget-flash > div { animation: dashWidgetFlash 1.6s ease-out 2; border-radius: 1rem; }
        /* Row-height uniform — carduri același înălțime pe același rând */
        .dash-uniform-rows { grid-auto-rows: minmax(200px, auto); }
        .dash-uniform-rows > div { min-height: 200px; }
        .dash-uniform-rows > div.row-span-2 { min-height: 416px; }
        .dash-uniform-rows > div > div { height: 100%; }
      `}</style>
      <style jsx global>{`
        .dash-sortable-ghost {
          opacity: 0.25;
        }
        .dash-sortable-chosen {
          cursor: grabbing;
        }
        .dash-sortable-drag {
          cursor: grabbing;
          box-shadow: 0 20px 40px rgba(241, 90, 36, 0.25) !important;
          transform: rotate(-0.5deg);
        }
      `}</style>
    </>
  );
}
